import React, { useCallback, useEffect, useRef, useState } from "react";

const KEY = "revq.cw.railw", DEF = 400, MIN = 360, CAP = 820, FORM_MIN = 560, EDIT_MIN = 560;
const readW = () => { try { const v = parseInt(localStorage.getItem(KEY), 10); return Number.isFinite(v) ? v : DEF; } catch { return DEF; } };
const saveW = (v) => { try { localStorage.setItem(KEY, String(v)); } catch { /* storage blocked */ } };

// Right-panel width for the claim form: saved width, clamped to the workspace; ≥560 while editing the note.
export function useRailWidth(editing) {
  const bodyRef = useRef(null);
  const [saved, setSaved] = useState(readW);
  const [bodyW, setBodyW] = useState(0);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    const el = bodyRef.current; if (!el) return undefined;
    const ro = new ResizeObserver(() => setBodyW(el.clientWidth));
    ro.observe(el); setBodyW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const max = Math.max(MIN, Math.min(CAP, (bodyW || 1920) - FORM_MIN));
  const clamp = useCallback((v) => Math.round(Math.max(MIN, Math.min(max, v))), [max]);
  const set = useCallback((v) => { const c = clamp(v); setSaved(c); saveW(c); }, [clamp]);
  const width = clamp(editing ? Math.max(saved, EDIT_MIN) : saved);
  return { bodyRef, width, saved: clamp(saved), min: MIN, max, set, dragging, setDragging };
}

export default function RailHandle({ rail }) {
  const start = useRef(null);
  const onDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { x: e.clientX, w: rail.width };
    rail.setDragging(true);
  };
  const onMove = (e) => { if (start.current) rail.set(start.current.w + (start.current.x - e.clientX)); };
  const onUp = (e) => { if (!start.current) return; start.current = null; rail.setDragging(false); try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ } };
  const onKey = (e) => {
    const step = e.shiftKey ? 64 : 16;
    const map = { ArrowLeft: rail.width + step, ArrowRight: rail.width - step, Home: rail.max, End: rail.min, Enter: 400 };
    if (map[e.key] == null) return;
    e.preventDefault(); rail.set(map[e.key]);
  };
  return (
    <div className="cfv-railhandle" role="separator" aria-orientation="vertical" aria-label="Resize inspector" tabIndex={0}
      aria-valuenow={rail.width} aria-valuemin={rail.min} aria-valuemax={rail.max}
      style={{ right: rail.width - 5 }} data-on={rail.dragging ? "1" : undefined}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      onDoubleClick={() => rail.set(400)} onKeyDown={onKey} data-testid="cfv-rail-handle" />
  );
}
